import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  DeletePestGroupRequest,
  DeletePestRequest,
  Pest,
  PestGroup,
  PestGroupListRequest,
  PestGroupListResponse,
  PestListRequest,
  PestListResponse,
  PutPestGroupRequest,
  PutPestRequest,
  ShowPestGroupRequest,
  ShowPestRequest,
} from "@/services/backend/pestcontrol/types/pest";

const pestcontrolPestApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "PestcontrolPest",
      "PestcontrolPests",
      "PestcontrolPestGroup",
      "PestcontrolPestGroups",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      pestcontrolPestGroupPut: build.mutation<PestGroup, PutPestGroupRequest>({
        query: (body) => ({
          url: "/pestcontrol/pest-groups",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolPestGroup", id: request.group.id },
          { type: "PestcontrolPestGroups", id: "LIST" },
          { type: "PestcontrolPests", id: "LIST" },
        ],
      }),
      pestcontrolPestGroupShow: build.query<PestGroup, ShowPestGroupRequest>({
        query: (params) => ({
          url: "/pestcontrol/pest-groups/get",
          method: "GET",
          params,
        }),
        providesTags: (result) =>
          result ? [{ type: "PestcontrolPestGroup", id: result.id }] : [],
      }),
      pestcontrolPestGroupDelete: build.mutation<void, DeletePestGroupRequest>({
        query: (params) => ({
          url: "/pestcontrol/pest-groups",
          method: "DELETE",
          params,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolPestGroup", id: request.id },
          { type: "PestcontrolPestGroups", id: "LIST" },
          { type: "PestcontrolPests", id: "LIST" },
        ],
      }),
      pestcontrolPestGroupList: build.query<
        PestGroupListResponse,
        PestGroupListRequest
      >({
        query: (params) => ({
          url: "/pestcontrol/pest-groups/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "PestcontrolPestGroups", id: "LIST" },
                ...result.items.map((group) => ({
                  type: "PestcontrolPestGroup" as const,
                  id: group.id,
                })),
              ]
            : [{ type: "PestcontrolPestGroups", id: "LIST" }],
      }),
      pestcontrolPestPut: build.mutation<Pest, PutPestRequest>({
        query: (body) => ({
          url: "/pestcontrol/pests",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolPest", id: request.pest.id },
          { type: "PestcontrolPests", id: "LIST" },
        ],
      }),
      pestcontrolPestShow: build.query<Pest, ShowPestRequest>({
        query: (params) => ({
          url: "/pestcontrol/pests/get",
          method: "GET",
          params,
        }),
        providesTags: (result) =>
          result ? [{ type: "PestcontrolPest", id: result.id }] : [],
      }),
      pestcontrolPestDelete: build.mutation<void, DeletePestRequest>({
        query: (params) => ({
          url: "/pestcontrol/pests",
          method: "DELETE",
          params,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolPest", id: request.id },
          { type: "PestcontrolPests", id: "LIST" },
          { type: "PestcontrolPestGroups", id: "LIST" },
        ],
      }),
      pestcontrolPestList: build.query<PestListResponse, PestListRequest>({
        query: (params) => ({
          url: "/pestcontrol/pests/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "PestcontrolPests", id: "LIST" },
                ...result.items.map((pest) => ({
                  type: "PestcontrolPest" as const,
                  id: pest.id,
                })),
              ]
            : [{ type: "PestcontrolPests", id: "LIST" }],
      }),
    }),
  });

export const {
  usePestcontrolPestGroupPutMutation,
  usePestcontrolPestGroupShowQuery,
  usePestcontrolPestGroupDeleteMutation,
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestPutMutation,
  usePestcontrolPestShowQuery,
  usePestcontrolPestDeleteMutation,
  usePestcontrolPestListQuery,
} = pestcontrolPestApi;
